import {
	DownloadFieldsFragment,
	DownloadsPageFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment
} from 'api/graphql-types';
import { GetStaticProps } from 'next';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetDownloads } from 'api/graphql-helper';
import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { ROUTES } from 'constants/routes';
import Downloads from 'modules/downloads/downloads';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { IBasePageProps } from 'types/page';

interface IDownloadsPageProps extends IBasePageProps {
	downloadsPage?: DownloadsPageFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
}

const DownloadsPage = (props: IDownloadsPageProps): React.JSX.Element => {
	const seoModule = props?.downloadsPage?.seo;

	const headerDefaultOptions = {
		backgroundColor: props.downloadsPage?.introHeader
			?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.downloadsPage?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={props.downloadsPage?.introHeader as IModule} slug={ROUTES.DOWNLOADS.fullPath} />
			</BaseModule>
			<BaseModule>
				{props.downloadsPage?.downloads?.map((module, index) => {
					return (
						<BaseModule defaultModuleOptions={module?.defaultModuleOptions} key={`module-${index}`}>
							<Downloads key={module?.downloadsTitle} module={module as DownloadFieldsFragment} />
						</BaseModule>
					);
				})}
			</BaseModule>
			<ModuleBuilder modules={props.downloadsPage?.modules} slug={ROUTES.DOWNLOADS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IDownloadsPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetDownloads(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, downloadsPage } = result.props.data;

		if (downloadsPage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				downloadsPage: downloadsPage.data?.attributes,
				id: downloadsPage.data?.documentId,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'downloads-page',
						contentTypeId: downloadsPage?.data?.documentId
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in downloads', error);

		return {
			notFound: true
		};
	}
};

export default DownloadsPage;
